import { CLOUDINARY_TRANSFORM_MAP } from './constants.js';
import {
  type CloudinaryTransformFlag,
  type CloudinaryTransformOptions,
} from './types.js';

const isCloudinaryOption = <T extends keyof CloudinaryTransformOptions>(
  option: [key: string, value: unknown],
): option is [
  key: T,
  value: Exclude<CloudinaryTransformOptions[T], undefined>,
] => {
  if (!option[1]) {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(
    CLOUDINARY_TRANSFORM_MAP,
    option[0],
  );
};

const isStringArrayValue = (
  value: Exclude<
    CloudinaryTransformOptions[keyof CloudinaryTransformOptions],
    undefined
  >,
): value is CloudinaryTransformFlag[] => {
  return Array.isArray(value);
};

const normalizeTransformOption = <T extends keyof CloudinaryTransformOptions>([
  key,
  value,
]: [key: T, value: Exclude<CloudinaryTransformOptions[T], undefined>]):
  | null
  | string => {
  if (value === undefined) {
    throw new Error(
      'Cloudinary option is `undefined`. This should never happen.',
    );
  }

  // If you get an error here, be sure you added any new options
  // added to `CloudinaryTransformOptions`, to `CLOUDINARY_TRANSFORM_MAP`.
  const transformKey = CLOUDINARY_TRANSFORM_MAP[key];

  // Set to null if value is array option and is empty
  let transformValue: null | string = isStringArrayValue(value)
    ? [...new Set(value)].sort((a, b) => a.localeCompare(b)).join('.') || null
    : value.toString();

  switch (key) {
    case 'pixelRatio': {
      const pixelRatio = Number(transformValue);

      if (pixelRatio <= 1) {
        return null;
      }

      transformValue = pixelRatio.toFixed(1);

      break;
    }

    default: {
      break;
    }
  }

  // This check is for array options that are empty.
  if (transformValue === null) return null;

  return [transformKey, transformValue].join('_');
};

export const buildCloudinaryTransforms = (
  ...transforms: CloudinaryTransformOptions[]
): string => {
  return transforms
    .map((transformOptions) => {
      return (
        Object.entries(transformOptions)
          .filter(isCloudinaryOption)
          .map((option) => normalizeTransformOption(option))
          // Normalizing can return `null` and we need to filter those out.
          .filter(
            (transformString): transformString is string =>
              transformString !== null,
          )
          // It is crucial to sort these alphabetically so we can hit the CDN cached eager versions
          .sort((a, b) => a.localeCompare(b))
          .join(',')
      );
    })
    .filter(Boolean)
    .join('/');
};
